import React, { useState, type ReactNode } from 'react';

export const DASHBOARD_LIST_PREVIEW_LIMIT = 8;

type PreviewItem = { id: number | string; name: string };

type Props<T extends PreviewItem> = {
  items: T[];
  emptyLabel?: string;
  className?: string;
  /** Custom row; default is a name-only `<li>`. */
  renderItem?: (item: T) => ReactNode;
  /** Rows shown before Show all; defaults to DASHBOARD_LIST_PREVIEW_LIMIT. */
  limit?: number;
  /** Custom collapsed button label; when set, the "· +N more" suffix is omitted. */
  showAllLabel?: (total: number) => string;
};

/** Expandable name list with preview cap + Show all. */
export default function PreviewNameList<T extends PreviewItem>({
  items,
  emptyLabel = 'None',
  className = '',
  renderItem,
  limit = DASHBOARD_LIST_PREVIEW_LIMIT,
  showAllLabel,
}: Props<T>) {
  const [showAll, setShowAll] = useState(false);

  if (items.length === 0) {
    return <div className={`small text-muted ${className}`.trim()}>{emptyLabel}</div>;
  }

  const visible = showAll ? items : items.slice(0, limit);
  const hidden = items.length - visible.length;

  return (
    <div className={className}>
      <ul className={`mb-0 ${renderItem ? 'ps-0 list-unstyled' : 'ps-3'} small`}>
        {visible.map((item) => {
          if (renderItem) {
            const node = renderItem(item);
            if (React.isValidElement(node)) {
              return React.cloneElement(node, { key: item.id });
            }
            return <li key={item.id}>{node}</li>;
          }
          return <li key={item.id}>{item.name}</li>;
        })}
      </ul>
      {items.length > limit ? (
        <button
          type="button"
          className="btn btn-link btn-sm px-0 py-0 mt-1"
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll
            ? 'Show less'
            : showAllLabel
              ? showAllLabel(items.length)
              : `Show all (${items.length})`}
          {!showAll && !showAllLabel && hidden > 0 ? ` · +${hidden} more` : null}
        </button>
      ) : null}
    </div>
  );
}
